import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  UserPlus,
  Trash,
  Pencil,
  Users,
  User,
  Folder,
  Filter,
  Settings,
  ChevronRight,
} from "lucide-react";

import { candidatesAPI2, candidateFoldersAPI, settingsAPI } from "@/services/api";
import { useToast } from "@/hooks/use-toast";

import AddCandidateModal from "@/components/admin/AddCandidateModal";
import EditCandidateModal from "@/components/admin/EditCandidateModal";
import CreateFolderModal from "@/components/admin/CreateFolderModal";
import FolderManagementModal from "@/components/admin/FolderManagementModal";

import { format } from "date-fns";

const FolderView = ({
  folderGroups = [],
  title,
  icon,
  folderPagination = { totalPages: 1, currentPage: 1 },
  folderPage,
  setFolderPage,
  itemsPerPage = 2,
  isMyCandidate = true,
  refetchFolders,
  refetchMy,
  refetchCompany,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [folderCandidates, setFolderCandidates] = useState([]);
  const [folderCandidatesPagination, setFolderCandidatesPagination] = useState({
    totalPages: 1,
    currentPage: 1,
    totalItems: 0,
  });
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [localPopupPage, setLocalPopupPage] = useState(1);
  const { toast } = useToast();

  const handleOpenCandidates = async (folder) => {
    setSelectedFolder(folder);
    setSearchTerm("");
    setLocalPopupPage(1);
    setIsLoadingCandidates(true);

    try {
      const response = await candidatesAPI2.getMyFolderCandidates({
        folderId: folder._id || folder.id,
        page: 1,
        limit: itemsPerPage,
        isMyCandidate,
      });

      const candidates = response.data?.data?.candidates || [];
      const pagination = response.data?.data?.pagination || {
        totalPages: 1,
        currentPage: 1,
        totalItems: 0,
      };

      setFolderCandidates(candidates);
      setFolderCandidatesPagination({
        totalPages: pagination.pages || pagination.totalPages || 1,
        currentPage: pagination.page || 1,
        totalItems: pagination.total || 0,
      });
      setIsDialogOpen(true);
    } catch (error) {
      console.error("Error fetching folder candidates:", error);
      toast({
        title: "Error",
        description: "Failed to load folder candidates",
        variant: "destructive",
      });
    } finally {
      setIsLoadingCandidates(false);
    }
  };

  // Real-time search handler - triggers on every keystroke
  const handleSearchFolderCandidates = async (searchValue) => {
    if (!selectedFolder) return;

    setIsLoadingCandidates(true);
    try {
      const response = await candidatesAPI2.getMyFolderCandidates({
        folderId: selectedFolder._id || selectedFolder.id,
        page: 1,
        limit: itemsPerPage,
        search: searchValue,
        isMyCandidate,
      });

      const candidates = response.data?.data?.candidates || [];
      const pagination = response.data?.data?.pagination || {
        totalPages: 1,
        currentPage: 1,
        totalItems: 0,
      };

      setFolderCandidates(candidates);
      setFolderCandidatesPagination({
        totalPages: pagination.pages || pagination.totalPages || 1,
        currentPage: 1,
        totalItems: pagination.total || pagination.totalItems || 0,
      });
      setLocalPopupPage(1);
    } catch (error) {
      console.error("Error searching folder candidates:", error);
      toast({
        title: "Error",
        description: "Failed to search candidates",
        variant: "destructive",
      });
    } finally {
      setIsLoadingCandidates(false);
    }
  };

  const handleFolderPageChange = async (newPage) => {
    if (!selectedFolder) return;

    setIsLoadingCandidates(true);
    try {
      const response = await candidatesAPI2.getMyFolderCandidates({
        folderId: selectedFolder._id || selectedFolder.id,
        page: newPage,
        limit: itemsPerPage,
        search: searchTerm,
        isMyCandidate,
      });

      const candidates = response.data?.data?.candidates || [];
      const pagination = response.data?.data?.pagination || {
        totalPages: 1,
        currentPage: newPage,
        totalItems: 0,
      };

      setFolderCandidates(candidates);
      setFolderCandidatesPagination({
        totalPages: pagination.pages || pagination.totalPages || 1,
        currentPage: newPage,
        totalItems: pagination.total || pagination.totalItems || 0,
      });
      setLocalPopupPage(newPage);
    } catch (error) {
      console.error("Error changing folder candidates page:", error);
      toast({
        title: "Error",
        description: "Failed to load page",
        variant: "destructive",
      });
    } finally {
      setIsLoadingCandidates(false);
    }
  };

  const handleDeleteCandidate = async (candidateId) => {
    if (!confirm("Are you sure you want to delete this candidate?")) return;

    try {
      // Delete API call
      await candidatesAPI2.delete(candidateId);

      toast({
        title: "Success",
        description: "Candidate deleted successfully",
        variant: "default",
      });

      // Refresh folders list (main screen)
      refetchFolders();
      
      // Refresh main candidate lists
      if (refetchMy) refetchMy();
      if (refetchCompany) refetchCompany();

      // Refresh candidates in the folder
      const response = await candidatesAPI2.getMyFolderCandidates({
        folderId: selectedFolder._id || selectedFolder.id,
        page: localPopupPage,
        limit: itemsPerPage,
        search: searchTerm,
        isMyCandidate,
      });

      const candidates = response.data?.data?.candidates || [];
      const pagination = response.data?.data?.pagination || {
        totalPages: 1,
        currentPage: localPopupPage,
        totalItems: 0,
      };

      setFolderCandidates(candidates);
      setFolderCandidatesPagination(pagination);

      // If there are no candidates left, close the popup
      if (candidates.length === 0) {
        setIsDialogOpen(false);
        return;
      }

      // If we deleted the last item on the page and it's not the first page, move to the previous page
      if (candidates.length === 0 && localPopupPage > 1) {
        handleFolderPageChange(localPopupPage - 1);
      }
    } catch (error) {
      console.error("Error deleting candidate:", error);
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to delete candidate",
        variant: "destructive",
      });
    }
  };

  const handleEditCandidate = (candidate) => {
    setSelectedCandidate(candidate);
    setIsEditModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <h3 className="text-lg font-semibold">{title}</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {folderGroups.map((folder) => (
          <Card
            key={folder.id || folder._id}
            className="hover:shadow-md transition-shadow cursor-pointer"
          >
            <CardContent className="p-4">
              <div className="flex flex-col items-center space-y-3">
                <div className="relative">
                  <Folder
                    className="h-16 w-16"
                    style={{ color: folder.color || "#3882F6" }}
                  />
                  {(folder.totalCandidates || folder.candidates?.length) >
                    0 && (
                    <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                      {folder.totalCandidates || folder.candidates?.length || 0}
                    </div>
                  )}
                </div>

                <div className="text-center w-full">
                  <h4
                    className="font-medium text-sm truncate"
                    title={folder.name}
                  >
                    {folder.name || "Unnamed Folder"}
                  </h4>
                  {folder.description && (
                    <p
                      className="text-xs text-gray-500 truncate mt-1"
                      title={folder.description}
                    >
                      {folder.description}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    {folder.totalCandidates || folder.candidates?.length || 0}{" "}
                    candidate
                    {(folder.totalCandidates !== 1 ||
                      folder.candidates?.length !== 1) &&
                      "s"}
                  </p>
                </div>

                {(folder.totalCandidates || folder.candidates?.length) > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={() => handleOpenCandidates(folder)}
                    disabled={isLoadingCandidates}
                  >
                    {isLoadingCandidates &&
                    selectedFolder?._id === folder._id ? (
                      "Loading..."
                    ) : (
                      <>
                        Show Candidates
                        <ChevronRight className="ml-2 h-3 w-3" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {folderPagination.totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setFolderPage((p) => Math.max(p - 1, 1))}
                  disabled={folderPage === 1}
                  className={
                    folderPage === 1
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer"
                  }
                />
              </PaginationItem>
              {Array.from(
                { length: folderPagination.totalPages },
                (_, i) => i + 1
              ).map((page) => (
                <PaginationItem key={page}>
                  <PaginationLink
                    isActive={folderPage === page}
                    onClick={() => setFolderPage(page)}
                    className={`cursor-pointer ${
                      folderPage === page
                        ? "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                        : ""
                    }`}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext
                  onClick={() =>
                    setFolderPage((p) =>
                      Math.min(p + 1, folderPagination.totalPages)
                    )
                  }
                  disabled={folderPage === folderPagination.totalPages}
                  className={
                    folderPage === folderPagination.totalPages
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer"
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {selectedFolder?.name || "Folder"} - Candidates
            </DialogTitle>
          </DialogHeader>

          <div className="flex justify-between items-center mb-4">
            <div className="relative flex-1">
              <Input
                placeholder="Search candidates..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  handleSearchFolderCandidates(e.target.value);
                }}
                className="w-full"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            {isLoadingCandidates ? (
              <div className="flex justify-center items-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary border-t-primary"></div>
              </div>
            ) : folderCandidates.length > 0 ? (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>S.No</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Mobile</TableHead>
                      <TableHead>Created At</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {folderCandidates.map((candidate, index) => (
                      <TableRow key={candidate._id || index}>
                        <TableCell>
                          {(localPopupPage - 1) * itemsPerPage + index + 1}
                        </TableCell>
                        <TableCell>{candidate.name || "N/A"}</TableCell>
                        <TableCell>{candidate.email || "N/A"}</TableCell>
                        <TableCell>{candidate.mobileNumber || "N/A"}</TableCell>
                        <TableCell>
                          {format(
                            new Date(candidate.createdAt),
                            "MMM dd, yyyy"
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditCandidate(candidate)}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500"
                            onClick={() =>
                              handleDeleteCandidate(
                                candidate._id || candidate.id
                              )
                            }
                          >
                            <Trash className="h-3 w-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {folderCandidatesPagination.totalPages > 1 && (
                  <div className="flex justify-center mt-4">
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            onClick={() =>
                              handleFolderPageChange(localPopupPage - 1)
                            }
                            disabled={localPopupPage === 1}
                            className={
                              localPopupPage === 1
                                ? "pointer-events-none opacity-50"
                                : "cursor-pointer"
                            }
                          />
                        </PaginationItem>
                        {Array.from(
                          {
                            length: folderCandidatesPagination.totalPages,
                          },
                          (_, i) => i + 1
                        ).map((page) => (
                          <PaginationItem key={page}>
                            <PaginationLink
                              isActive={localPopupPage === page}
                              onClick={() => handleFolderPageChange(page)}
                              className={`cursor-pointer ${
                                localPopupPage === page
                                  ? "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                                  : ""
                              }`}
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationItem>
                          <PaginationNext
                            onClick={() =>
                              handleFolderPageChange(localPopupPage + 1)
                            }
                            disabled={
                              localPopupPage ===
                              folderCandidatesPagination.totalPages
                            }
                            className={
                              localPopupPage ===
                              folderCandidatesPagination.totalPages
                                ? "pointer-events-none opacity-50"
                                : "cursor-pointer"
                            }
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </>
            ) : (
              <p className="text-center text-gray-500">
                {searchTerm
                  ? "No candidates match your search."
                  : "No candidates found in this folder."}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {isEditModalOpen && (
        <EditCandidateModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedCandidate(null);
          }}
          candidateId={selectedCandidate.id}
          onSuccess={() => {
            handleFolderPageChange(localPopupPage);
            setIsEditModalOpen(false);
            refetchFolders();
            if (refetchMy) refetchMy();
            if (refetchCompany) refetchCompany();
          }}
        />
      )}
    </div>
  );
};

const Candidates = () => {
  const PAGE_LIMIT = 5;
  const [mySearchTerm, setMySearchTerm] = useState("");
  const [otherSearchTerm, setOtherSearchTerm] = useState("");
  const [copage, setCopage] = useState(1);
  const [cpage, setCpage] = useState(1);
  const [selectedFolder, setSelectedFolder] = useState("all");
  const [viewMode, setViewMode] = useState("folder");
  const [expandedFolders, setExpandedFolders] = useState(
    new Set(["my-candidates", "other-candidates"])
  );
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [isFolderManagementOpen, setIsFolderManagementOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [editingFolder, setEditingFolder] = useState(null);

  const { toast } = useToast();

  const {
    data: myData,
    isLoading: isLoadingMy,
    isError: isErrorMy,
    refetch: refetchMy,
  } = useQuery({
    queryKey: ["my-candidates", copage],
    queryFn: async () => {
      try {
        const response = await candidatesAPI2.getMy({
          page: copage,
          limit: PAGE_LIMIT,
        });
        return (
          response.data || {
            data: [],
            pagination: {
              totalPages: 1,
              currentPage: 1,
              totalItems: 0,
              itemsPerPage: PAGE_LIMIT,
            },
            totalCandidates: 0,
          }
        );
      } catch (error) {
        console.error("My Candidates Error:", error);
        return {
          data: [],
          pagination: {
            totalPages: 1,
            currentPage: 1,
            totalItems: 0,
            itemsPerPage: PAGE_LIMIT,
          },
          totalCandidates: 0,
        };
      }
    },
  });

  const {
    data: companyData,
    isLoading: isLoadingCompany,
    isError: isErrorCompany,
    refetch: refetchCompany,
  } = useQuery({
    queryKey: ["company-candidates", cpage],
    queryFn: async () => {
      try {
        const response = await candidatesAPI2.getCompany({
          page: cpage,
          limit: PAGE_LIMIT,
        });
        return (
          response.data || {
            data: [],
            pagination: {
              totalPages: 1,
              currentPage: 1,
              totalItems: 0,
              itemsPerPage: PAGE_LIMIT,
            },
            totalCandidates: 0,
          }
        );
      } catch (error) {
        console.error("Company Candidates Error:", error);
        return {
          data: [],
          pagination: {
            totalPages: 1,
            currentPage: 1,
            totalItems: 0,
            itemsPerPage: PAGE_LIMIT,
          },
          totalCandidates: 0,
        };
      }
    },
  });

  const { data: folderData, refetch: refetchFolders } = useQuery({
    queryKey: ["folders-filter"],
    queryFn: () => candidateFoldersAPI.getMy({ limit: 100 }),
  });

  const handleMySearch = (e) => {
    e.preventDefault();
    setCopage(1);
    refetchMy();
  };

  const handleOtherSearch = (e) => {
    e.preventDefault();
    setCpage(1);
    refetchCompany();
  };

  const handleFolderChange = (folderId) => {
    setSelectedFolder(folderId);
    setCopage(1);
    setCpage(1);
  };

  const handleDeleteCandidate = async (id) => {
    if (!confirm("Are you sure you want to delete this candidate?")) return;
    try {
      await candidatesAPI2.delete(id);
      toast({
        title: "Candidate deleted",
        description: "The candidate has been deleted successfully",
      });
      refetchMy();
      refetchCompany();
      refetchFolders();
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to delete candidate",
        variant: "destructive",
      });
    }
  };

  const handleEditCandidate = (candidate) => {
    setSelectedCandidate(candidate);
    setIsEditModalOpen(true);
  };

  const [folderModalRefetchCallback, setFolderModalRefetchCallback] = useState(null);

  const handleCreateFolder = (refetchCallback) => {
    setEditingFolder(null);
    setFolderModalRefetchCallback(() => refetchCallback); // Store the refetch callback
    setIsFolderModalOpen(true);
  };

  const handleEditFolder = (folder, refetchCallback) => {
    setEditingFolder(folder);
    setFolderModalRefetchCallback(() => refetchCallback); // Store the refetch callback
    setIsFolderModalOpen(true);
  };

  const toggleFolderExpansion = (folderId) => {
    const newExpanded = new Set(expandedFolders);
    newExpanded.has(folderId)
      ? newExpanded.delete(folderId)
      : newExpanded.add(folderId);
    setExpandedFolders(newExpanded);
  };

  const handleAddCandidateClick = async () => {
    // Fetch and log cloud storage settings from database
    try {
      const response = await settingsAPI.getCloudStorageSettings();
      if (response.data.success) {
        // Check if all required settings are configured
        const isConfigured = response.data.data.bucket && 
                           response.data.data.access_key && 
                           response.data.data.secret_key;
        
      }
    } catch (error) {
      console.error("Failed to fetch cloud storage settings:", error);
    }
    
    // Open the Add Candidate modal
    setIsAddModalOpen(true);
  };

  const renderCandidateRow = (candidate, index, showSno = true) => (
    <TableRow key={candidate._id}>
      {showSno && <TableCell className="font-medium">{index + 1}</TableCell>}
      <TableCell className="font-medium">{candidate.name}</TableCell>
      <TableCell>{candidate.email}</TableCell>
      <TableCell>{candidate.mobileNumber || "N/A"}</TableCell>
      <TableCell>
        {candidate.folderId ? (
          <div className="flex items-center">
            <div
              className="mr-2 h-3 w-3 rounded-full"
              style={{ backgroundColor: candidate.folderId.color }}
            />
            <span className="text-sm">{candidate.folderId.name}</span>
          </div>
        ) : (
          <div className="flex items-center text-gray-500">
            <Folder className="mr-2 h-3 w-3" />
            <span className="text-sm">Others</span>
          </div>
        )}
      </TableCell>
      <TableCell>{candidate.createdBy?.name || "N/A"}</TableCell>
      <TableCell>
        {format(new Date(candidate.createdAt), "MMM dd, yyyy")}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end space-x-1">
          <Button
            variant="ghost"
            size="icon"
            title="Edit"
            onClick={() => handleEditCandidate(candidate)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-red-500"
            onClick={() => handleDeleteCandidate(candidate._id)}
            title="Delete"
          >
            <Trash className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );

  const renderIndividualView = (
    candidates,
    title,
    icon,
    searchTerm,
    setSearchTerm,
    onSearch,
    page,
    setPage,
    pagination = {
      totalItems: 0,
      itemsPerPage: PAGE_LIMIT,
      totalPages: 1,
      currentPage: 1,
    }
  ) => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <h3 className="text-lg font-semibold">{title}</h3>
        <span className="text-sm text-gray-500">({pagination.totalItems})</span>
      </div>

      {candidates.length > 0 ? (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>S.No</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead>Folder</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead>Created Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {candidates.map((candidate, index) =>
                  renderCandidateRow(
                    candidate,
                    (page - 1) * pagination.itemsPerPage + index,
                    true
                  )
                )}
              </TableBody>
            </Table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="flex justify-center mt-4">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => setPage((p) => Math.max(p - 1, 1))}
                      disabled={page === 1}
                      className={
                        page === 1
                          ? "pointer-events-none opacity-50"
                          : "cursor-pointer"
                      }
                    />
                  </PaginationItem>
                  {Array.from(
                    { length: pagination.totalPages },
                    (_, i) => i + 1
                  ).map((pageNum) => (
                    <PaginationItem key={pageNum}>
                      <PaginationLink
                        isActive={page === pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`cursor-pointer ${
                          page === pageNum
                            ? "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                            : ""
                        }`}
                      >
                        {pageNum}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  <PaginationItem>
                    <PaginationNext
                      onClick={() =>
                        setPage((p) => Math.min(p + 1, pagination.totalPages))
                      }
                      disabled={page === pagination.totalPages}
                      className={
                        page === pagination.totalPages
                          ? "pointer-events-none opacity-50"
                          : "cursor-pointer"
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </>
      ) : (
        <div className="text-center p-6 text-gray-500 border rounded-md">
          No candidates found in this category
        </div>
      )}
    </div>
  );

  const processFolders = (data) => {
    if (!data) return [];
    return data.data || [];
  };

  const folders = folderData?.data?.data || [];
  const myCandidates = myData || {
    data: [],
    pagination: {
      totalPages: 1,
      currentPage: 1,
      totalItems: 0,
      itemsPerPage: PAGE_LIMIT,
    },
    totalCandidates: 0,
  };
  const companyCandidates = companyData || {
    data: [],
    pagination: {
      totalPages: 1,
      currentPage: 1,
      totalItems: 0,
      itemsPerPage: PAGE_LIMIT,
    },
    totalCandidates: 0,
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Candidates Management</h1>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Filter className="w-4 h-4 mr-2" />
                View By: {viewMode === "individual" ? "Individual" : "Folder"}
              </Button>
            </DropdownMenuTrigger>
          </DropdownMenu>
          <Button
            variant="outline"
            onClick={() => setIsFolderManagementOpen(true)}
          >
            <Settings className="w-4 h-4 mr-2" /> Manage Folders
          </Button>
          <Button onClick={handleAddCandidateClick}>
            <UserPlus className="w-4 h-4 mr-2" /> Add New Candidate
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          {isLoadingMy || isLoadingCompany ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary border-t-primary"></div>
            </div>
          ) : isErrorMy || isErrorCompany ? (
            <div className="text-center p-6 text-red-500">
              Failed to load candidates. Please try again.
            </div>
          ) : (
            <Accordion
              type="multiple"
              value={Array.from(expandedFolders)}
              onValueChange={setExpandedFolders}
              className="w-full"
            >
              <AccordionItem value="my-candidates">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    <span>
                      My Candidates (
                      {myCandidates.pagination?.totalItems ||
                        myCandidates.totalCandidates ||
                        0}
                      )
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {viewMode === "folder" ? (
                    <FolderView
                      folderGroups={processFolders(myData)}
                      title="Candidates created by you"
                      icon={<User className="h-5 w-5 text-blue-600" />}
                      folderPagination={
                        myCandidates.pagination || {
                          totalPages: 1,
                          currentPage: 1,
                        }
                      }
                      folderPage={copage}
                      setFolderPage={setCopage}
                      itemsPerPage={PAGE_LIMIT}
                      isMyCandidate={true}
                      refetchFolders={refetchFolders}
                      refetchMy={refetchMy}
                      refetchCompany={refetchCompany}
                    />
                  ) : (
                    renderIndividualView(
                      myCandidates.data || [],
                      "Candidates created by you",
                      <User className="h-5 w-5 text-blue-600" />,
                      mySearchTerm,
                      setMySearchTerm,
                      handleMySearch,
                      copage,
                      setCopage,
                      myCandidates.pagination || {
                        totalItems: 0,
                        itemsPerPage: PAGE_LIMIT,
                        totalPages: 1,
                        currentPage: 1,
                      }
                    )
                  )}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="other-candidates">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    <span>
                      Company Candidates (
                      {companyCandidates.pagination?.totalItems ||
                        companyCandidates.totalCandidates ||
                        0}
                      )
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {viewMode === "folder" ? (
                    <FolderView
                      folderGroups={processFolders(companyData)}
                      title="Candidates created by other company users"
                      icon={<Users className="h-5 w-5 text-green-600" />}
                      folderPagination={
                        companyCandidates.pagination || {
                          totalPages: 1,
                          currentPage: 1,
                        }
                      }
                      folderPage={cpage}
                      setFolderPage={setCpage}
                      itemsPerPage={PAGE_LIMIT}
                      isMyCandidate={false}
                      refetchFolders={refetchFolders}
                      refetchMy={refetchMy}
                      refetchCompany={refetchCompany}
                    />
                  ) : (
                    renderIndividualView(
                      companyCandidates.data || [],
                      "Candidates created by other company users",
                      <Users className="h-5 w-5 text-green-600" />,
                      otherSearchTerm,
                      setOtherSearchTerm,
                      handleOtherSearch,
                      cpage,
                      setCpage,
                      companyCandidates.pagination || {
                        totalItems: 0,
                        itemsPerPage: PAGE_LIMIT,
                        totalPages: 1,
                        currentPage: 1,
                      }
                    )
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}
        </CardContent>
      </Card>

      <AddCandidateModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => {
          refetchMy();
          refetchCompany();
          refetchFolders();
        }}
      />

      <EditCandidateModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        candidate={selectedCandidate}
        onSuccess={() => {
          refetchMy();
          refetchCompany();
          refetchFolders();
        }}
      />

      <CreateFolderModal
        isOpen={isFolderModalOpen}
        onClose={() => setIsFolderModalOpen(false)}
        onSuccess={() => {
          refetchMy();
          refetchCompany();
          refetchFolders();
          setIsFolderModalOpen(false);
          // Call the refetch callback if it exists (from FolderManagementModal)
          if (folderModalRefetchCallback) {
            folderModalRefetchCallback();
          }
        }}
        editFolder={editingFolder}
      />

      <FolderManagementModal
        isOpen={isFolderManagementOpen}
        onClose={() => setIsFolderManagementOpen(false)}
        onCreateFolder={handleCreateFolder}
        onEditFolder={handleEditFolder}
        onFolderCreatedOrEdited={() => {
          // This will be called after folder creation/edit to refetch in the modal
          refetchMy();
          refetchCompany();
          refetchFolders();
        }}
      />
    </div>
  );
};

export default Candidates;